import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { analyticsChartTheme, formatCompactMetric } from 'util/chartUtils';
import ButtonGrp from '../../../../components/kea/ButtonGrp';
import IntlMessages from '../../../../util/IntlMessages';
import DashboardPanel from './DashboardPanel';
import styles from './DashboardChart.module.scss';

function Profitability({ chart }) {
  const localizeMessage = useLocalizedMessage();
  const empty = !chart || Boolean(chart.error) || !chart.weekly || !chart.daily;
  const barIsWeekly = !empty && chart.daily.money.curve.length > 25;
  const [weeklyBar, toggleBarType] = useState(barIsWeekly);
  const source = !empty ? (weeklyBar ? chart.weekly : chart.daily) : null;
  const data = source ? source.money.curve : [];
  const action = (
    <ButtonGrp
      value={weeklyBar}
      buttons={[
        { value: true, text: localizeMessage('dashboard.profitability.weekly') },
        { value: false, text: localizeMessage('dashboard.profitability.daily') },
      ]}
      onClick={toggleBarType}
    />
  );

  return (
    <DashboardPanel
      title={<IntlMessages id="dashboard.profitability.header" />}
      subtitle={<IntlMessages id="dashboard.profitability.subheader" />}
      note={<IntlMessages id="dashboard.profitability.subsubheader" />}
      action={action}
      tone="warning"
      empty={empty}
      emptyVariant="bars"
    >
      <div className={styles.chartFrame} role="img" aria-label={localizeMessage('dashboard.profitability.header')}>
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={data} margin={{ top: 12, right: 18, left: 0, bottom: 12 }}>
            <CartesianGrid stroke={analyticsChartTheme.grid} vertical={false} strokeDasharray="2 7" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} minTickGap={26} tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }} />
            <YAxis axisLine={false} tickLine={false} width={48} tickFormatter={formatCompactMetric} tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }} />
            <Tooltip />
            <Legend />
            <Bar name={localizeMessage('dashboard.profitability.chart.otherItems')} dataKey="noSearch" stackId="cart" fill={analyticsChartTheme.previous} radius={[0, 0, 4, 4]} isAnimationActive={false} />
            <Bar name={localizeMessage('dashboard.profitability.chart.searchItems')} dataKey="search" stackId="cart" fill={analyticsChartTheme.searches} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardPanel>
  );
}

Profitability.propTypes = {
  chart: PropTypes.shape({
    weekly: PropTypes.shape({ money: PropTypes.shape({ curve: PropTypes.arrayOf(PropTypes.shape({})) }) }),
    daily: PropTypes.shape({ money: PropTypes.shape({ curve: PropTypes.arrayOf(PropTypes.shape({})) }) }),
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
};

Profitability.defaultProps = { chart: null };

export default Profitability;
